import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Empty, Modal, Select, Switch, Table, Tag, Tooltip, notification } from "antd";
import { DeleteOutlined, EditOutlined, PlayCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import VoiceBrowserTest from "pages/SurveyManagement/VoiceBrowserTest";
import { languageForUi } from "pages/Sonic/languageUtils";
import { SURVEY_RESPONSE_MODE, VOICE_MODELS } from "pages/SurveyManagement/constants";
import { listSurveys } from "pages/SurveyManagement/surveyService";
import { createAgentKnowledge, deleteAgentKnowledge, updateAgentKnowledge } from "util/agentKnowledgeApi";
import KnowledgeRecordModal from "./KnowledgeRecordModal";
import { knowledgeErrorText } from "./knowledgeErrors";
import styles from "./style.module.scss";

const ALL = "__all__";

// The company's ready answers and topic guidelines, shared by every voice survey.
const AgentKnowledgeTab = ({ data, onChanged }) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState(ALL);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [voiceSurveys, setVoiceSurveys] = useState(null);
  const [testSurveyId, setTestSurveyId] = useState(null);
  // The voice model for this test only; starts at the survey's saved model and is not saved.
  const [testVoiceModel, setTestVoiceModel] = useState(null);

  const canEdit = data.can_manage && !data.read_only;
  const companyRecords = useMemo(() => (data.records || []).filter((r) => !r.instrument_id), [data.records]);
  const counts = useMemo(() => {
    const acc = {};
    companyRecords.forEach((r) => {
      acc[r.topic] = (acc[r.topic] || 0) + 1;
    });
    return acc;
  }, [companyRecords]);
  const visible = topic === ALL ? companyRecords : companyRecords.filter((r) => r.topic === topic);

  const fail = (res) =>
    notification.error({ message: knowledgeErrorText(t, res), placement: "bottomRight" });

  const save = async (values) => {
    setSaving(true);
    const res = editing ? await updateAgentKnowledge(editing.id, values) : await createAgentKnowledge(values);
    setSaving(false);
    if (!res || res.error) {
      fail(res);
      return;
    }
    setModalOpen(false);
    setEditing(null);
    onChanged();
  };

  const toggleActive = async (record, active) => {
    setBusyId(record.id);
    const res = await updateAgentKnowledge(record.id, { active });
    setBusyId(null);
    if (!res || res.error) fail(res);
    else onChanged();
  };

  const remove = (record) =>
    Modal.confirm({
      title: t("surveySettings.knowledge.delete_title"),
      content: t("surveySettings.knowledge.delete_body"),
      okText: t("surveySettings.knowledge.delete_ok"),
      okButtonProps: { danger: true },
      cancelText: t("surveySettings.knowledge.cancel"),
      onOk: async () => {
        const res = await deleteAgentKnowledge(record.id);
        if (!res || res.error) fail(res);
        else onChanged();
      },
    });

  const openTest = async () => {
    setTestOpen(true);
    if (voiceSurveys) return;
    const surveys = await listSurveys().catch(() => []);
    const voice = surveys.filter(
      (s) => s.responseMode === SURVEY_RESPONSE_MODE.VOICE && s.distribution?.phone_call?.exists
    );
    setVoiceSurveys(voice);
    if (voice.length) setTestSurveyId(voice[0].id);
  };
  const pickTestSurvey = (id) => {
    setTestSurveyId(id);
    setTestVoiceModel(null);
  };
  const testSurvey = (voiceSurveys || []).find((s) => s.id === testSurveyId);
  const savedVoiceModel = testSurvey?.distribution?.phone_call?.voiceModel || "";
  const voiceModel = testVoiceModel === null ? savedVoiceModel : testVoiceModel;

  const columns = [
    {
      title: t("surveySettings.knowledge.col_kind"),
      dataIndex: "kind",
      width: 130,
      render: (kind) => (
        <Tag className={styles.kindTag} color={kind === "answer" ? "purple" : "cyan"}>
          {t(kind === "answer" ? "surveySettings.knowledge.kind_answer" : "surveySettings.knowledge.kind_guideline")}
        </Tag>
      ),
    },
    { title: t("surveySettings.knowledge.col_topic"), dataIndex: "topic", width: 130 },
    {
      title: t("surveySettings.knowledge.col_text"),
      key: "text",
      render: (_, r) => (
        <div>
          {r.kind === "answer" ? <div className={styles.trigger}>{r.trigger}</div> : null}
          <div className={styles.response}>
            {r.kind === "answer" ? "→ " : ""}
            {r.response}
          </div>
        </div>
      ),
    },
    {
      title: t("surveySettings.knowledge.col_after"),
      dataIndex: "after_action",
      width: 130,
      render: (after, r) =>
        r.kind === "answer"
          ? t(after === "end_call" ? "surveySettings.knowledge.after_end_call" : "surveySettings.knowledge.after_continue")
          : "—",
    },
    {
      title: t("surveySettings.knowledge.col_updated"),
      key: "updated",
      width: 150,
      render: (_, r) => (
        <span className={styles.meta}>
          {r.updated_by_email || "—"}
          <br />
          {r.updated_at ? dayjs(r.updated_at).format("D MMM YYYY HH:mm") : ""}
        </span>
      ),
    },
    {
      title: t("surveySettings.knowledge.col_active"),
      dataIndex: "active",
      width: 80,
      render: (active, r) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          loading={busyId === r.id}
          onChange={(value) => toggleActive(r, value)}
        />
      ),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            width: 90,
            render: (_, r) => (
              <span>
                <Tooltip title={t("surveySettings.knowledge.edit")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditing(r);
                      setModalOpen(true);
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("surveySettings.knowledge.delete")}>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(r)} />
                </Tooltip>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <p className={styles.intro}>
          {t(canEdit ? "surveySettings.knowledge.intro" : "surveySettings.knowledge.intro_read_only")}
        </p>
        <div className={styles.headerActions}>
          <Button icon={<PlayCircleOutlined />} onClick={openTest}>
            {t("surveySettings.knowledge.test")}
          </Button>
          {canEdit ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              {t("surveySettings.knowledge.add")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className={styles.topicChips}>
        <Tag.CheckableTag checked={topic === ALL} onChange={() => setTopic(ALL)}>
          {t("surveySettings.knowledge.all_topics")} {companyRecords.length}
        </Tag.CheckableTag>
        {(data.topics || [])
          .filter((name) => counts[name])
          .map((name) => (
            <Tag.CheckableTag key={name} checked={topic === name} onChange={() => setTopic(name)}>
              {name} {counts[name]}
            </Tag.CheckableTag>
          ))}
      </div>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={visible}
        pagination={false}
        rowClassName={(r) => (r.active ? "" : styles.inactiveRow)}
        locale={{ emptyText: <Empty description={t("surveySettings.knowledge.empty")} /> }}
      />

      <KnowledgeRecordModal
        open={modalOpen}
        record={editing}
        topics={data.topics}
        saving={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />

      <Modal
        open={testOpen}
        title={t("surveySettings.knowledge.test_title")}
        footer={null}
        width={720}
        onCancel={() => setTestOpen(false)}
        destroyOnClose
      >
        {voiceSurveys === null ? null : voiceSurveys.length === 0 ? (
          <Empty description={t("surveySettings.knowledge.test_no_survey")} />
        ) : (
          <>
            <div className={styles.testIntro}>
              <p className={styles.intro}>{t("surveySettings.knowledge.test_intro")}</p>
              <Select
                className={styles.fullWidth}
                value={testSurveyId}
                onChange={pickTestSurvey}
                options={voiceSurveys.map((s) => ({ value: s.id, label: s.name }))}
              />
              <label className={styles.testLabel}>{t("surveyManagement.distribute.phone.voice_model")}</label>
              <Select
                className={styles.fullWidth}
                value={voiceModel}
                onChange={(v) => setTestVoiceModel(v || "")}
                options={VOICE_MODELS.map((m) => ({ value: m.value, label: t(m.labelKey) }))}
              />
              <p className={styles.testHint}>{t("surveySettings.knowledge.test_voice_model_hint")}</p>
            </div>
            {testSurvey ? (
              <VoiceBrowserTest
                key={`${testSurvey.id}:${voiceModel}`}
                instrumentId={testSurvey.id}
                surveyTitle={testSurvey.name}
                questions={testSurvey.questions || []}
                phoneCall={{ ...testSurvey.distribution.phone_call, voiceModel }}
                language={
                  testSurvey.distribution.phone_call.language ||
                  languageForUi(testSurvey.primaryLanguage || i18n.language)
                }
                hint={t("surveyManagement.distribute.phone.test_hint_saved")}
              />
            ) : null}
          </>
        )}
      </Modal>
    </div>
  );
};

export default AgentKnowledgeTab;
